// Steel Plate Configuration

export const APP_NAME = 'iSteelPlate';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

import type { DropdownOption, ChartImage } from '../shared';

export type APIPayload = {
  title: string;
  Lx: number;
  Ly: number;
  tpl: number;
  damping: number;
  addedWeight: string;
  orientation: number;
  support: number;
  Fdy: string;
  Fdu: string;
  E: string;
  density: number;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: number; // initial displacement
  v0: number; // initial velocity
};

export type ChartKey = 'ductility' | 'hysteresis' | 'load' | 'displacement' | 'rotation' | 'reaction';

export const SUPPORT_TYPES_STEEL_PLATE: DropdownOption[] = [
  { value: 1, label: 'Pinned All Around' },
  { value: 2, label: 'Fixed All Around' }
];

export type APIResponse = {
  // Scalar Results
  TA: number;
  Me: number;
  Tn: number;
  K: number;
  Xmax: number;
  tmax: number;
  KLM: number;
  steel_weight: number;
  GWP: number;
  duct: number;
  rot: number;

  // History / Array Data
  ru: number;
  xe: number;

  Vx: number[];
  Vy: number[];

  // Charts (optional + partial means "some keys may be missing")
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

// ============================================================================
// RESPONSE DISPLAY CONFIGURATION
// ============================================================================

import type { SectionConfig } from '../shared';
import { createElement as h } from 'react';

/**
 * Configuration for displaying response data
 * Defines what fields to show and how to format them
 */
export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Results',
    fields: [
      { key: 'TA', label: 'Tributary Area', getValue: data => h('span', null, data.TA.toFixed(2), ' (ft', h('sup', null, '2'), ')') },
      {
        label: 'Stiffness',
        getValue: data => h('span', null, data.K.toFixed(2), ' (kips/in)')
      },
      {
        key: 'Me',
        label: 'Effective Mass',
        getValue: data => h('span', null, data.Me.toFixed(2), ' (lb-sec', h('sup', null, '2'), '/in)')
      },
      {
        label: 'KLM',
        getValue: data => `${data.KLM.toFixed(2)}`
      },
      {
        label: 'Period',
        getValue: data => h('span', null, data.Tn.toFixed(2), ' (msec)')
      },
      {
        label: 'Resistance',
        getValue: data => `${data.ru.toFixed(2)} (psi)`
      },
      {
        label: 'Ductility',
        getValue: data => `${data.duct.toFixed(2)}`
      },
      {
        label: 'Rotation',
        getValue: data => h('span', null, data.rot.toFixed(2), ' (deg)')
      },
      {
        label: 'Max Displacement',
        getValue: data => h('span', null, data.Xmax.toFixed(2), ' (in) @ ', data.tmax.toFixed(2), ' msec')
      }
    ]
  },
  {
    title: 'Other Results',
    fields: [
      {
        key: 'Vx',
        label: 'Vx Reaction',
        getValue: data => `[ ${data.Vx.map(value => value.toFixed(2))} (lb/in)`
      },
      {
        key: 'Vy',
        label: 'Vy Reaction',
        getValue: data => `[ ${data.Vy.map(value => value.toFixed(2))} (lb/in)`
      },
      {
        key: 'steel_weight',
        label: 'Steel Weight',
        getValue: data => h('span', null, data.steel_weight.toFixed(2), ' (lbs)')
      },
      {
        key: 'GWP',
        label: 'GWP',
        getValue: data => h('span', null, data.GWP.toFixed(2), ' (kg CO2e)')
      }
    ]
  }
];
